/**
 * Light or dark, and nothing else.
 *
 * The portal used to carry the app's five themes. Since the 2026-09-21 redesign the members'
 * site has one look with a light and a dark half, and light is the default: this is read at a
 * desk, in daylight, and a dark page by default reads as a night-time product. "system" is still
 * honoured when she chooses it, but it is not what a new visitor gets.
 */

export const MODES = ['light', 'dark', 'system'] as const
export type Mode = (typeof MODES)[number]

// A new key: the old site wrote 'system' for every visitor whether she chose it or not, so
// honouring it would make dark the default for everyone who had ever visited.
const MODE_KEY = 'covella.mode.v2'
const DEFAULT_MODE: Mode = 'light'

// localStorage throws in private-browsing configurations rather than returning null, and a
// preference is never worth failing a page load over.
function read(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* preference is cosmetic; losing it is not an error worth surfacing */
  }
}

export function storedMode(): Mode {
  const raw = read(MODE_KEY, DEFAULT_MODE)
  return (MODES as readonly string[]).includes(raw) ? (raw as Mode) : DEFAULT_MODE
}

export function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveMode(mode: Mode): 'light' | 'dark' {
  return mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode
}

/**
 * Resolves "system" to a real value and stamps it onto <html>.
 *
 * The same resolution runs inline in index.html before first paint. If this changes, that has
 * to change too, or a dark-mode member gets a white flash on every load.
 */
export function applyMode(mode: Mode): void {
  document.documentElement.setAttribute('data-mode', resolveMode(mode))
  write(MODE_KEY, mode)
}
